const BASE_SRC = '/images/label-layout-base.png';
const TRIM_SRC = '/images/label-layout-trim.png';

const GRADIENT_DARK = { r: 0, g: 0, b: 0 };
const GRADIENT_LIGHT = { r: 255, g: 255, b: 255 };

export type LabelMode = 'illustration' | 'label-full' | 'label-trimmed';

export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

type DrawableImage = HTMLImageElement | HTMLCanvasElement;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

function applyGradientMap(img: DrawableImage, targetWidth: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const sw = img.width;
  const sh = img.height;
  const scale = targetWidth / sw;
  canvas.width = targetWidth;
  canvas.height = Math.ceil(sh * scale);

  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;

  for (let i = 0; i < pixels.length; i += 4) {
    const brightness =
      (0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]) / 255;
    pixels[i] = GRADIENT_DARK.r + (GRADIENT_LIGHT.r - GRADIENT_DARK.r) * brightness;
    pixels[i + 1] = GRADIENT_DARK.g + (GRADIENT_LIGHT.g - GRADIENT_DARK.g) * brightness;
    pixels[i + 2] = GRADIENT_DARK.b + (GRADIENT_LIGHT.b - GRADIENT_DARK.b) * brightness;
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

export async function compositeLabel(
  artworkSrc: string,
  mode: LabelMode,
  crop?: CropRect | null,
  cropRef?: { w: number; h: number } | null,
): Promise<Blob> {
  if (mode === 'illustration') {
    const artwork = await loadImage(artworkSrc);
    const canvas = document.createElement('canvas');
    canvas.width = artwork.naturalWidth;
    canvas.height = artwork.naturalHeight;
    canvas.getContext('2d')!.drawImage(artwork, 0, 0);
    return await canvasToBlob(canvas);
  }

  const [base, artwork, trim] = await Promise.all([
    loadImage(BASE_SRC),
    loadImage(artworkSrc),
    loadImage(TRIM_SRC),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = base.width;
  canvas.height = base.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

  const scaledCrop = scaleCropToArtwork(crop, cropRef, artwork);
  const artSource: DrawableImage = scaledCrop
    ? cropToCanvas(artwork, scaledCrop)
    : artwork;
  const mappedArt = applyGradientMap(artSource, artSource.width);
  const xOffset = Math.round((canvas.width - mappedArt.width) / 2);
  const yOffset = Math.round(canvas.height * 0.23);
  ctx.drawImage(mappedArt, xOffset, yOffset);
  ctx.drawImage(base, 0, 0);

  if (mode === 'label-full') {
    return await canvasToBlob(canvas);
  }

  // label-trimmed: use inverted trim alpha as transparency mask
  const trimCanvas = document.createElement('canvas');
  trimCanvas.width = canvas.width;
  trimCanvas.height = canvas.height;
  const trimCtx = trimCanvas.getContext('2d', { willReadFrequently: true })!;
  trimCtx.drawImage(trim, 0, 0, canvas.width, canvas.height);
  const trimData = trimCtx.getImageData(0, 0, canvas.width, canvas.height);

  const labelData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const labelPx = labelData.data;
  const trimPx = trimData.data;

  for (let i = 0; i < labelPx.length; i += 4) {
    const invertedTrimAlpha = 255 - trimPx[i + 3];
    labelPx[i + 3] = Math.round((labelPx[i + 3] * invertedTrimAlpha) / 255);
  }

  ctx.putImageData(labelData, 0, 0);
  return await canvasToBlob(canvas);
}

function scaleCropToArtwork(
  crop: CropRect | null | undefined,
  ref: { w: number; h: number } | null | undefined,
  artwork: HTMLImageElement,
): CropRect | null {
  if (!crop || crop.w <= 0 || crop.h <= 0) return null;
  const refW = ref && ref.w > 0 ? ref.w : artwork.naturalWidth;
  const refH = ref && ref.h > 0 ? ref.h : artwork.naturalHeight;
  const sx = artwork.naturalWidth / refW;
  const sy = artwork.naturalHeight / refH;
  if (sx === 1 && sy === 1) return crop;
  return { x: crop.x * sx, y: crop.y * sy, w: crop.w * sx, h: crop.h * sy };
}

function cropToCanvas(img: HTMLImageElement, crop: CropRect): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(crop.w));
  c.height = Math.max(1, Math.round(crop.h));
  c.getContext('2d')!.drawImage(
    img,
    crop.x, crop.y, crop.w, crop.h,
    0, 0, c.width, c.height,
  );
  return c;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error('Canvas toBlob returned null'));
    }, 'image/png');
  });
}
